// src/components/evenement/FormulaireInscriptionCI.jsx
import { useState, useEffect } from 'react';
import { FaSpinner, FaCheckCircle, FaExclamationCircle, FaTimes, FaUser, FaEnvelope, FaPhone, FaArrowRight, FaGraduationCap } from 'react-icons/fa';
import WhatsappStep from './WhatsappStep';
import './FormulaireInscriptionCI.css';

function FormulaireInscriptionCI({ onClose }) {
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: ''
  });

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [showWhatsapp, setShowWhatsapp] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // ========== CONFIGURATION AIRTABLE ==========
  // ✅ Même configuration que l'original
  const AIRTABLE_API_KEY = 'patrUA4eqzY98yxuR.92896d8e382c74bae17472f970f7ffab5edb5a85714e9680bb26035e0bd5b1a6';
  const AIRTABLE_BASE_ID = 'appbZfcP0IDtmwPU2';
  const AIRTABLE_TABLE_NAME = "Formulaire d'inscription"; // ← Même table que l'original
  // ===========================================

  // ✅ Bloquer le scroll du body quand la modal est ouverte
  useEffect(() => {
    const scrollY = window.scrollY;
    
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
    
    return () => {
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      window.scrollTo(0, parseInt(scrollY || '0') * -1);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) errors.nom = 'Le nom est requis';
    if (!formData.prenom.trim()) errors.prenom = 'Le prénom est requis';
    if (!formData.email.trim()) {
      errors.email = 'L\'email est requis';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Email invalide';
    }
    
    if (!formData.telephone.trim()) {
      errors.telephone = 'Le numéro de téléphone est requis';
    } else {
      const cleanPhone = formData.telephone.replace(/\D/g, '');
      if (cleanPhone.length < 10) {
        errors.telephone = 'Numéro de téléphone invalide (10 chiffres requis)';
      } else if (isNaN(parseInt(cleanPhone, 10))) {
        errors.telephone = 'Format de téléphone invalide';
      }
    }
    
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // 🔧 Nettoyer le numéro et le convertir en nombre
      const cleanPhone = formData.telephone.replace(/\D/g, '');
      const telephoneNumber = parseInt(cleanPhone, 10);
      
      if (isNaN(telephoneNumber) || cleanPhone.length < 10) {
        throw new Error('Numéro de téléphone invalide. Veuillez entrer 10 chiffres.');
      }
      
      const airtableData = {
        records: [{
          fields: {
            "Nom": formData.nom.trim(),
            "Prénom": formData.prenom.trim(),
            "Email": formData.email.trim().toLowerCase(),
            "Téléphone": telephoneNumber
          }
        }]
      };
      
      console.log('📤 Envoi vers Airtable...', airtableData);
      console.log('📱 Téléphone (nombre) :', telephoneNumber, typeof telephoneNumber);
      
      const response = await fetch(`https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${encodeURIComponent(AIRTABLE_TABLE_NAME)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${AIRTABLE_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(airtableData)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('Erreur Airtable:', errorData);
        
        if (response.status === 422) {
          const fieldName = errorData.error?.message?.match(/"([^"]+)"/)?.[1] || 'champ';
          throw new Error(`Erreur dans le champ "${fieldName}". Vérifiez le format des données.`);
        } else if (response.status === 404) {
          throw new Error(`Table "${AIRTABLE_TABLE_NAME}" non trouvée. Vérifiez le nom.`);
        } else if (response.status === 403) {
          throw new Error('Permission refusée. Vérifiez votre token API.');
        } else {
          throw new Error(errorData.error?.message || 'Erreur lors de l\'inscription');
        }
      }
      
      const result = await response.json();
      console.log('✅ Inscription réussie !', result);

      // ============================================
      // 🎯 INTÉGRATION PIXEL META - ÉVÉNEMENT LEAD
      // ============================================
      // ✅ Inscription réussie → notification Meta
      if (window.fbq) {
        window.fbq('track', 'Lead', {
          content_name: 'Inscription Forum ETEF Côte d\'Ivoire',
          content_category: 'forum_inscription_ci',
          // Données supplémentaires pour enrichir le tracking
          nom: formData.nom.trim(),
          prenom: formData.prenom.trim(),
          email: formData.email.trim().toLowerCase()
        });
        console.log('📊 Pixel Meta - Événement Lead envoyé');
      } else {
        console.warn('⚠️ Pixel Meta non disponible (bloqueur ou non chargé)');
      }
      // ============================================
      
      // Au lieu de fermer directement, on montre l'étape WhatsApp
      setSuccess(true);
      
      // Attendre un peu puis passer à l'étape WhatsApp
      setTimeout(() => {
        setShowWhatsapp(true);
        setLoading(false);
      }, 1500);
      
    } catch (err) {
      console.error('Erreur détaillée:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handleWhatsappFinish = () => {
    setFormData({ nom: '', prenom: '', email: '', telephone: '' });
    setSuccess(false);
    setShowWhatsapp(false);
    onClose();
  };

  // Si on est à l'étape WhatsApp, afficher le composant WhatsApp
  if (showWhatsapp) {
    return (
      <div className="formModalOverlay" onClick={onClose}>
        <div className="formModalContent" onClick={(e) => e.stopPropagation()}>
          <button className="formCloseBtn" onClick={handleWhatsappFinish}>
            <FaTimes />
          </button>
          <WhatsappStep 
            onClose={onClose}
            onFinish={handleWhatsappFinish}
            userName={formData.prenom}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="formModalOverlay" onClick={onClose}>
      <div className="formModalContent" onClick={(e) => e.stopPropagation()}>
        <button className="formCloseBtn" onClick={onClose}>
          <FaTimes />
        </button>
        
        <div className="formHeader">
          <div className="formHeaderIcon">
            <FaGraduationCap />
          </div>
          <h2>Inscription au <span className="formHeaderGradient">Forum de Préparation au Départ en France</span></h2>
          <p>Participation gratuite - Places limitées</p>
        </div>
        
        {success ? (
          <div className="formSuccessMessage">
            <FaCheckCircle className="formSuccessIcon" />
            <h3>Inscription réussie !</h3>
            <p>Redirection en cours...</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="formContainer">
            <div className="formRow">
              <div className="formGroup">
                <label>
                  <FaUser className="formLabelIcon" />
                  Nom *
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className={fieldErrors.nom ? 'formError' : ''}
                  placeholder="Votre nom"
                />
                {fieldErrors.nom && <span className="formErrorMessage">{fieldErrors.nom}</span>}
              </div>
              
              <div className="formGroup">
                <label>
                  <FaUser className="formLabelIcon" />
                  Prénom *
                </label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className={fieldErrors.prenom ? 'formError' : ''}
                  placeholder="Votre prénom"
                />
                {fieldErrors.prenom && <span className="formErrorMessage">{fieldErrors.prenom}</span>}
              </div>
            </div>
            
            <div className="formGroup">
              <label>
                <FaEnvelope className="formLabelIcon" />
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={fieldErrors.email ? 'formError' : ''}
                placeholder="votre@email.com"
              />
              {fieldErrors.email && <span className="formErrorMessage">{fieldErrors.email}</span>}
            </div>
            
            <div className="formGroup">
              <label>
                <FaPhone className="formLabelIcon" />
                Numéro de téléphone * (10 chiffres)
              </label>
              <input
                type="tel"
                name="telephone"
                value={formData.telephone}
                onChange={handleChange}
                className={fieldErrors.telephone ? 'formError' : ''}
                placeholder="0612345678"
              />
              {fieldErrors.telephone && <span className="formErrorMessage">{fieldErrors.telephone}</span>}
            </div>
            
            {error && (
              <div className="formErrorMessageGlobal">
                <FaExclamationCircle />
                <span>{error}</span>
              </div>
            )}
            
            <button type="submit" className="formSubmitBtn" disabled={loading}>
              {loading ? (
                <>
                  <FaSpinner className="formSpinner" />
                  Envoi en cours...
                </>
              ) : (
                <>
                  S'inscrire gratuitement
                  <FaArrowRight />
                </>
              )}
            </button>
            
            <p className="formLegal">
              En vous inscrivant, vous acceptez de recevoir des communications de l'ETEF.
              Vos données sont protégées et ne seront jamais partagées.
            </p>
          </form>
        )}
      </div>
    </div>
  );
}

export default FormulaireInscriptionCI;